import type { ClientLibraryId } from 'types/client-library'
import type { Thunk } from 'types/redux'
import api from '@disco/api/server'
import {
  CLIENT_LIBRARY_CONTENT_LOAD,
  CLIENT_LIBRARY_CONTENT_LOAD_FAIL,
  CLIENT_LIBRARY_CONTENT_LOAD_SUCCESS,
  CLIENT_LIBRARY_CONTENT_UPDATE,
  CLIENT_LIBRARY_CONTENT_UPDATE_FAIL,
  CLIENT_LIBRARY_CONTENT_UPDATE_SUCCESS,
} from '@disco/constants/ActionTypes'
import { pushUINotification } from '@disco/utils/notifications'
/**
 * Loads all content for a client library by paginating through all pages.
 * @param libraryId - ID of the client library to load content for
 * @returns Thunk that loads all content across multiple pages and handles success/error states
 */
export function clientLibraryContentLoad(libraryId: ClientLibraryId): Thunk {
  return async dispatch => {
    dispatch({
      type: CLIENT_LIBRARY_CONTENT_LOAD,
      libraryId,
    })

    try {
      let response = await api.getClientLibraryContent(libraryId)
      const results = response.results

      while (response.next) {
        response = await api.getUrl(
          response.next,
          null,
          'api.getClientLibraryContentNext',
        )
        results.push(...response.results)
      }

      dispatch({
        type: CLIENT_LIBRARY_CONTENT_LOAD_SUCCESS,
        libraryId,
        response: {
          ...response,
          results,
        },
      })
    } catch (error) {
      dispatch({
        type: CLIENT_LIBRARY_CONTENT_LOAD_FAIL,
        libraryId,
        error,
      })
    }
  }
}
/**
 * Updates content in a client library by adding and removing specified groups.
 * @param libraryId - ID of the client library to update content for
 * @param to_add - Array of group IDs to add to the library
 * @param to_remove - Array of group IDs to remove from the library
 * @returns Thunk that updates content and shows success notification
 */
export function clientLibraryContentUpdate(
  libraryId: ClientLibraryId,
  to_add: number[],
  to_remove: number[],
): Thunk {
  return async dispatch => {
    dispatch({
      type: CLIENT_LIBRARY_CONTENT_UPDATE,
      libraryId,
      payload: {
        to_add,
        to_remove,
      },
    })

    try {
      const response = await api.alterClientLibraryGroups(
        libraryId,
        to_add,
        to_remove,
      )
      dispatch({
        type: CLIENT_LIBRARY_CONTENT_UPDATE_SUCCESS,
        libraryId,
        response,
      })
      pushUINotification({
        message: 'Content updated!',
        level: 'success',
      })
    } catch (error) {
      dispatch({
        type: CLIENT_LIBRARY_CONTENT_UPDATE_FAIL,
        libraryId,
        error,
      })
    }
  }
}
